import { AnyAction } from 'redux';
import { IFieldGroup, IProducer } from '../../pages/producer-list/interfaces';

const initState: any = {
  fullProducerList: [],
  selectedProducers: {},
  statusFilter: null,
  searchFilter: null,
  sortBy: {
    field: 'name',
    sortDirection: 'asc',
  },
  groupFilter: {},
  journeyInformation: null,
  producerInformation: null,
  cycleInformation: null,
  downloadingBoundaries: false,
  producerListState: { lifeCycle: 'NONE' },
  producerInformationState: { lifeCycle: 'NONE' },
  journeyInformationState: { lifeCycle: 'NONE' },
  cycleInformationState: { lifeCycle: 'NONE' },
};

const getFilterResult = (statusFilter: string, producerItem: any) => {
  if (!statusFilter) {
    return true;
  }
  return producerItem.case.status === statusFilter;
};

const getSortField = (producer: any, fieldName: string) => {
  if (fieldName === 'status') {
    return producer.case.status;
  }
  return producer[fieldName];
};

const getCompareResult = (field: string, producer1: any, producer2: any) => {
  return getSortField(producer1, field).localeCompare(getSortField(producer2, field));
};

const producerComparator = (sortBy: any, producer1: any, producer2: any) => {
  const { field, sortDirection } = sortBy;
  const ascSortResult = getCompareResult(field, producer1, producer2);
  return sortDirection === 'asc' ? ascSortResult : ascSortResult * -1;
};

const getSearchFilter = (searchFilter: string, producerItem: any) => {
  if (!searchFilter) {
    return true;
  }
  if (producerItem.case.status.toLowerCase().indexOf(searchFilter.toLowerCase()) !== -1) {
    return true;
  }
  if (producerItem.name.toLowerCase().indexOf(searchFilter.toLowerCase()) !== -1) {
    return true;
  }
  if (producerItem.location.toLowerCase().indexOf(searchFilter.toLowerCase()) !== -1) {
    return true;
  }
  return false;
};

const getGroupFilter = (groupFilter: any, producerItem: IProducer) => {
  for (const [key, value] of Object.entries(groupFilter)) {
    if (value === true && producerItem.fieldGroups.find((gp: IFieldGroup) => gp.name === key)) return true;
  }
  return Object.keys(groupFilter).length === 0 || !Object.values(groupFilter).includes(true);
};

const getProcessedProducerList = (fullProducerList: Array<any>, statusFilter: string, searchFilter: string, sortBy: any, groupFilter: any) => {
  return fullProducerList
    .filter((producer: any) => getFilterResult(statusFilter, producer))
    .filter((producer: any) => getSearchFilter(searchFilter, producer))
    .filter((producer: any) => getGroupFilter(groupFilter, producer))
    .sort((producer1, producer2) => producerComparator(sortBy, producer1, producer2));
};

const getSortDirection = (actionSortBy: any, stateSortBy: any) => {
  if (actionSortBy.field === stateSortBy.field) {
    return stateSortBy.sortDirection === 'asc' ? 'desc' : 'asc';
  }
  return 'asc';
};

const reducer = (state = initState, action: AnyAction) => {
  switch (action.type) {
    case 'GET_PRODUCER_LIST_SUCCEEDED': {
      if (['SUCCESS', 'ERROR'].indexOf(state.cycleInformationState.lifeCycle) !== -1) {
        for (const producer of action.payload) {
          producer.cycles = state.cycleInformation[producer.opportunityId] || state.cycleInformation[producer.leadId] || {};
        }
      }
      state = {
        ...state,
        fullProducerList: action.payload,
        processedProducerList: getProcessedProducerList(action.payload, state.statusFilter, state.searchFilter, state.sortBy, state.groupFilter),
        producerListState: { lifeCycle: 'SUCCESS' },
      };
      return state;
    }
    case 'GET_PRODUCER_BY_ID_SUCCEEDED': {
      const countryMap = {
        'United States': 'us',
        Brazil: 'br',
      };
      const countryFromAPI = action.payload?.Country ?? action.payload?.country;
      return {
        ...state,
        producerInformation: {
          ...action.payload,
          country: countryMap[countryFromAPI],
        },
        producerInformationState: { lifeCycle: 'SUCCESS' },
      };
    }
    case 'GET_JOURNEY_BY_ID_SUCCEEDED': {
      return {
        ...state,
        journeyInformation: action.payload,
        journeyInformationState: { lifeCycle: 'SUCCESS' },
      };
    }
    case 'PRODUCER_STATUS_FILTERED': {
      return {
        ...state,
        statusFilter: action.payload,
        processedProducerList: getProcessedProducerList(state.fullProducerList, action.payload, state.searchFilter, state.sortBy, state.groupFilter),
      };
    }
    case 'PRODUCER_SEARCH_APPLIED': {
      return {
        ...state,
        searchFilter: action.payload,
        processedProducerList: getProcessedProducerList(state.fullProducerList, state.statusFilter, action.payload, state.sortBy, state.groupFilter),
      };
    }
    case 'PRODUCER_SORT_CLICKED': {
      const newSortBy = {
        field: action.payload.field,
        sortDirection: getSortDirection(action.payload, state.sortBy),
      };
      return {
        ...state,
        sortBy: newSortBy,
        processedProducerList: getProcessedProducerList(state.fullProducerList, state.statusFilter, state.searchFilter, newSortBy, state.groupFilter),
      };
    }
    case 'VERIFICATION_GROUP_FILTERED': {
      return {
        ...state,
        groupFilter: action.payload,
        processedProducerList: getProcessedProducerList(state.fullProducerList, state.statusFilter, state.searchFilter, state.sortBy, action.payload),
      };
    }
    case 'CLEAR_PRODUCER_FILTERS': {
      const defaultSortBy = {
        field: 'name',
        sortDirection: 'asc',
      };
      return {
        ...state,
        statusFilter: null,
        searchFilter: null,
        groupFilter: {},
        sortBy: defaultSortBy,
        processedProducerList: getProcessedProducerList(state.fullProducerList, '', '', defaultSortBy, {}),
      };
    }
    case 'CLEAR_PRODUCER_STORE': {
      return {
        ...state,
        journeyInformation: null,
        producerInformation: null,
        producerListState: { lifeCycle: 'NONE' },
        producerInformationState: { lifeCycle: 'NONE' },
        journeyInformationState: { lifeCycle: 'NONE' },
      };
    }
    case 'DOWNLOAD_BOUNDARIES_REQUESTED': {
      return {
        ...state,
        downloadingBoundaries: true,
      };
    }
    case 'DOWNLOAD_BOUNDARIES_SUCCEEDED':
    case 'DOWNLOAD_BOUNDARIES_ERROR': {
      return {
        ...state,
        downloadingBoundaries: false,
      };
    }
    case 'SELECT_PRODUCER': {
      if (state.selectedProducers[action.payload.opportunityId] === undefined) {
        return {
          ...state,
          selectedProducers: {
            ...state.selectedProducers,
            [action.payload.opportunityId]: action.payload.journeyId ?? null,
          },
        };
      }
      delete state.selectedProducers[action.payload.opportunityId];
      return {
        ...state,
        selectedProducers: {
          ...state.selectedProducers,
        },
      };
    }
    case 'SELECT_ALL_PRODUCERS': {
      return {
        ...state,
        selectedProducers: action.payload,
      };
    }
  }
  return state;
};

export default reducer;
